import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/PhpAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../components/ui/use-toast';
import { apiRequest } from '../config/api';
import { Stethoscope, User, Lock, Eye, EyeOff } from 'lucide-react';

const AdminLoginPage = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, profile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await apiRequest('get_login_users.php');
        console.log('Admin login users response:', data); // Debug log
        if (data.success) {
          // Filter to show only admin users
          const adminUsers = (data.users || []).filter(user => user.role === 'admin');
          setUsers(adminUsers);
          if (adminUsers.length === 0) {
            console.warn('No admin users found for login dropdown');
          }
        } else {
          console.error('Failed to fetch users:', data.error || data.message);
          toast({
            title: 'Warning',
            description: 'Could not load user list. Please refresh the page.',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        toast({
          title: 'Error',
          description: 'Failed to load users. Please check your connection.',
          variant: 'destructive'
        });
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please select a user and enter password',
        variant: 'destructive'
      });
      return;
    }
    setLoading(true);
    try {
      const { error } = await signIn(formData.username, formData.password);
      if (!error) {
        toast({
          title: 'Welcome back!',
          description: 'Successfully logged in.',
        });
        // Navigation will happen automatically via useEffect when profile is set
      } else {
        toast({
          title: 'Login Failed',
          description: error.message || 'Invalid credentials',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Only redirect if we're on the admin login page and user successfully logs in
  React.useEffect(() => {
    // Only redirect if we're actually on the admin login page
    const currentPath = window.location.pathname;
    if (currentPath !== '/admin/login') {
      return; // Don't redirect if we're not on the admin login page
    }
    
    if (profile) {
      console.log('AdminLoginPage: Profile detected, redirecting based on role:', profile.role);
      if (profile.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        // If logged in user is not admin, redirect to appropriate dashboard
        switch (profile.role) {
          case 'patient':
            navigate('/patient/dashboard', { replace: true });
            break;
          case 'doctor':
          case 'chief_doctor':
            navigate('/doctor/dashboard', { replace: true });
            break;
          default:
            navigate('/', { replace: true });
        }
      }
    }
  }, [profile, navigate]);

  return (
    <>
      <Helmet>
        <title>Admin Sign In - Gugan Dental Care</title>
        <meta name="description" content="Sign in to your admin account to access the admin dashboard for Gugan Dental Care." />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-effect border-gray-700">
            <CardHeader className="text-center p-4 sm:p-6">
              <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
                <Stethoscope className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                <span className="text-xl sm:text-2xl font-bold gradient-text">Gugan Dental Care</span>
              </div>
              <CardTitle className="text-xl sm:text-2xl text-white">Admin Login</CardTitle>
              <CardDescription className="text-gray-300 text-sm sm:text-base">
                Sign in to your admin account to continue
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-white">Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    {loadingUsers ? (
                      <Input
                        id="username"
                        placeholder="Loading users..."
                        className="pl-10 bg-gray-800 border-gray-600 text-white"
                        disabled
                      />
                    ) : (
                      <Select 
                        value={formData.username} 
                        onValueChange={(value) => setFormData({ ...formData, username: value })}
                      >
                        <SelectTrigger id="username" className="pl-10 bg-gray-800 border-gray-600 text-white">
                          <SelectValue placeholder="Select admin to login" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          {users.length > 0 ? (
                            users
                              .filter(user => {
                                // Only show active admins in dropdown
                                return user.status === 'Active' || !user.status;
                              })
                              .map((user) => {
                                const displayName = (user.full_name || user.username || '').toUpperCase();
                                return (
                                  <SelectItem 
                                    key={user.id} 
                                    value={user.username}
                                    className="text-white hover:bg-gray-700"
                                  >
                                    {displayName}
                                  </SelectItem>
                                );
                              })
                          ) : (
                            <SelectItem value="" disabled className="text-gray-500">
                              No admin users available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full medical-gradient text-white hover:opacity-90"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default AdminLoginPage;

